import type { PaypointFormData } from "Components/Paypoints/schema";
import { FormSection } from "Components/Shared/FormSection";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { type MerchantFacilityDto, PayPointType } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { Resources, useResource } from "Translations/Resources";
import type { Control } from "react-hook-form";

const RESOURCES = Resources.Paypoint.Form;

type Props = {
  control: Control<PaypointFormData>;
  facilities: Array<MerchantFacilityDto>;
  // Which second type sits next to NFC; the type is not selectable at all when neither applies.
  isGatewayProduct: boolean;
  isTerminalProduct: boolean;
  // Facility and type are fixed by the product and cannot be edited.
  isLocked: boolean;
  onTypeChange: (value: string | number) => void;
};

export const PaypointGeneralInfoSection = ({
  control,
  facilities,
  isGatewayProduct,
  isTerminalProduct,
  isLocked,
  onTypeChange,
}: Props) => {
  const { t } = useResource();

  const facilityCodeList: Array<CodeListItem> = facilities.map(
    ({ publicID, name }) => ({ code: publicID, name }),
  );

  // Types selectable for this product's paypoints: OrbitM/L → NFC + Terminal, OrbitS → NFC + Gateway.
  const typeCodeList: Array<CodeListItem> = isGatewayProduct
    ? [
        { code: PayPointType.NFC, name: t(RESOURCES.TypeNFC) },
        { code: PayPointType.Gateway, name: t(RESOURCES.TypeGateway) },
      ]
    : [
        { code: PayPointType.NFC, name: t(RESOURCES.TypeNFC) },
        { code: PayPointType.Terminal, name: t(RESOURCES.TypeTerminal) },
      ];

  return (
    <FormSection legend={t(RESOURCES.Legend.GeneralInfo)}>
      <FormInput control={control} name="name" label={t(RESOURCES.Name)} />
      <FormInput
        control={control}
        name="description"
        label={t(RESOURCES.Description)}
        type="text"
        multiline
        rows={4}
      />
      <Dropdown
        codeList={facilityCodeList}
        name="facilityPublicID"
        control={control}
        label={t(RESOURCES.Facility)}
        disabled={isLocked}
      />
      {(isTerminalProduct || isGatewayProduct) && (
        <Dropdown
          codeList={typeCodeList}
          name="type"
          control={control}
          label={t(RESOURCES.Type)}
          disabled={isLocked}
          onChange={onTypeChange}
        />
      )}
    </FormSection>
  );
};
